import React, { useState } from "react";
import PageHeader from "components/PageHeader";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import useTabParam from "components/hooks/useTabParam";
import DSContainer from "../../system-design/DSContainer";
import DSBox from "../../system-design/DSBox";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import MonthlyCalendarView from "./MonthlyCalendarView";
import TodayCalendarEventsList from "./TodayEventsList";
import WeekEventsTab from "./WeekEventsTab";
import FeedHistoryTab from "./FeedHistoryTab";
import CalendarEventsList from "./CalendarEventsList";
import UnifiedCalendarEventModal from "./UnifiedCalendarEventModal";

import { GeographicRegion, State } from "../../../api/calendarEvent";

// Internal tab keys ↔ URL-friendly values ("CALENDAR EVENTS" has a space)
const TAB_KEY_TO_URL: Record<string, string> = {
  "CALENDAR EVENTS": "events",
};
const TAB_URL_TO_KEY: Record<string, string> = {
  events: "CALENDAR EVENTS",
};

type CalendarEventCalendarProps = {
  defaultCountry?: GeographicRegion | null;
  defaultState?: State | null;
  defaultThreshold?: number;
  defaultLimit?: number;
};

const CalendarEventCalendar = ({
  defaultCountry,
  defaultState,
}: CalendarEventCalendarProps) => {
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [tabParam, setTabParam] = useTabParam("tab", "events", [
    "events",
    "monthly",
    "today",
    "week",
    "feedHistory",
  ]);
  const activeTab = tabParam
    ? (TAB_URL_TO_KEY[tabParam] ?? tabParam)
    : undefined;
  const setActiveTab = (key: string | undefined) =>
    setTabParam(key ? (TAB_KEY_TO_URL[key] ?? key) : undefined);

  const tabs: {
    key: string;
    label: string;
    content: React.ReactNode;
    /** set while a view is not ready to be opened (e.g. loading) */
    disabled?: boolean;
  }[] = [
    {
      key: "CALENDAR EVENTS",
      label: "Calendar Events",
      content: <CalendarEventsList />,
    },
    {
      key: "monthly",
      label: "Monthly View",
      content: (
        <MonthlyCalendarView
          defaultCountry={defaultCountry}
          defaultState={defaultState}
        />
      ),
    },
    {
      key: "today",
      label: "Today's Events",
      content: <TodayCalendarEventsList />,
    },
    {
      key: "week",
      label: "Week Events",
      content: <WeekEventsTab />,
    },
    {
      key: "feedHistory",
      label: "Feed History",
      content: <FeedHistoryTab />,
    },
  ];

  const activeTabContent = activeTab
    ? tabs.find((tab) => tab.key === activeTab)?.content
    : null;

  return (
    <DSContainer maxWidth="xl">
      <PageHeader
        overline="Content"
        title="Scheduler"
        subtitle="Holiday and event scheduling for the klydo feed"
        actions={
          <DSPrimaryButton onClick={() => setShowAddEvent(true)}>
            + Add Event
          </DSPrimaryButton>
        }
        tabs={
          <DSSegmentedTabs
            options={tabs.map((tab) => ({
              label: tab.label,
              value: tab.key,
              disabled: tab.disabled,
            }))}
            value={activeTab}
            onChange={setActiveTab}
          />
        }
      />

      <DSBox sx={{ pb: 3 }}>{activeTabContent}</DSBox>

      {showAddEvent && (
        <UnifiedCalendarEventModal
          open={showAddEvent}
          onClose={() => setShowAddEvent(false)}
          minDate={new Date()}
        />
      )}
    </DSContainer>
  );
};

export default CalendarEventCalendar;
